import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getTeam } from "@/lib/api";
import { Mail, User, Crown } from "lucide-react";
import { Link } from "react-router-dom";

export default function TeamSection() {
  const [members, setMembers] = useState([]);
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    getTeam().then((res) => setMembers(res.data)).catch(() => {});
  }, []);

  // Separate permanent (first 4) and associate members
  const permanentMembers = members.slice(0, 4);
  const associateMembers = members.slice(4);

  return (
    <section id="team" data-testid="team-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1670B9] mb-4">
            Notre équipe
          </p>
          <h2
            className="font-['Quicksand'] text-3xl lg:text-4xl font-medium tracking-tight text-slate-800 mb-4"
            data-testid="team-title"
          >
            Les chercheurs d'UR-EBioReZ
          </h2>
          <p className="text-base text-slate-500 max-w-xl mx-auto">
            Une équipe pluridisciplinaire de {members.length} chercheurs permanents et associés
          </p>
        </motion.div>

        {members.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg">L'équipe sera ajoutée via le panneau d'administration.</p>
          </div>
        ) : (
          <>
            {/* Permanent members - highlighted */}
            {permanentMembers.length > 0 && (
              <div className="mb-16">
                <div className="flex items-center gap-2 mb-8 justify-center">
                  <Crown className="w-4 h-4 text-[#F49C03]" />
                  <h3 className="font-['Quicksand'] text-lg font-semibold text-slate-700">Membres permanents</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {permanentMembers.map((member, i) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.5, delay: i * 0.08 }}
                      className="group text-center"
                      data-testid={`team-member-${i}`}
                    >
                      <div className="relative w-36 h-36 mx-auto mb-5 rounded-xl overflow-hidden shadow-sm">
                        {member.photo_url ? (
                          <img
                            src={member.photo_url}
                            alt={member.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1670B9]/10 to-[#74CDD9]/10">
                            <User className="w-14 h-14 text-[#1670B9]/30" />
                          </div>
                        )}
                        {i === 0 && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#F49C03] rounded-md flex items-center justify-center shadow-sm">
                            <Crown className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </div>
                      <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800">
                        {member.name}
                      </h3>
                      <p className="text-xs text-[#1670B9] font-medium mt-1 leading-snug px-2">{member.role}</p>
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#1670B9] mt-2 transition-colors"
                        >
                          <Mail className="w-3 h-3" /> {member.email}
                        </a>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Associate members - compact grid */}
            {associateMembers.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-8 justify-center">
                  <h3 className="font-['Quicksand'] text-lg font-semibold text-slate-700">Membres associés</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {associateMembers.map((member, i) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.3 + i * 0.05 }}
                      className="flex items-center gap-3 bg-[#FAFAFA] rounded-xl p-3 border border-slate-100 hover:shadow-sm transition-shadow"
                      data-testid={`team-assoc-${i}`}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#1670B9]/10 to-[#74CDD9]/10 flex items-center justify-center">
                        {member.photo_url ? (
                          <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-[#1670B9]/40" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{member.name}</p>
                        <p className="text-xs text-slate-400 truncate">{member.role}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
